import React from 'react';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import usePageMeta from '../../hooks/usePageMeta';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(10),
    maxWidth: 900,
    textAlign: 'left',
  },
  section: {
    marginTop: theme.spacing(5),
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
  },
  updated: {
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

export default function Terms() {
  const classes = useStyles();
  usePageMeta('Terms of Service', 'Terms of service for The Warrior Tribe Chrome extension and website.');

  return (
    <Container className={classes.root}>
      <Typography variant="h3" gutterBottom>
        Terms of Service
      </Typography>
      <Typography variant="subtitle1" className={classes.updated}>
        Last updated: June 1, 2026
      </Typography>

      <Typography variant="body1" className={classes.paragraph}>
        These Terms of Service ("Terms") govern your access to and use of The Warrior Tribe website at{' '}
        <Link href="https://www.thewarriortribe.xyz" color="primary">thewarriortribe.xyz</Link> and the Mr. NoFap Chrome extension (collectively, the "Service"). By using the Service, you agree to these Terms. If you do not agree, do not use the Service.
      </Typography>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>1. The Service</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Warrior Tribe provides a Chrome browser extension that blocks access to adult websites, together with a web-based profile system that tracks experience points (XP) and streaks. The Service is designed to support individuals who have chosen to reduce or eliminate compulsive consumption of explicit online content.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Service is a self-management tool. It is not a medical device, a licensed therapeutic service, or a substitute for professional mental health treatment. If you are experiencing a clinical addiction or mental health condition, please seek appropriate professional support.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>2. Eligibility</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You must be at least 13 years of age to use the Service. By using the Service, you confirm that you meet this requirement. If you are under 18, you should have the permission of a parent or guardian.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>3. Account and Google Sign-In</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Service uses Google Sign-In via the Chrome identity API to identify your account. Your Google account email address is used as your unique identifier. You are responsible for maintaining the security of the Google account you use with the Service. We are not responsible for any loss or damage resulting from unauthorized access to your Google account.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>4. Subscriptions and Billing</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Service offers a 14-day free trial upon installation of the Chrome extension. After the trial period, continued access to the blocking and XP features requires a paid subscription. Available subscription plans include monthly, half-year, and annual options.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Payments are processed securely through Stripe. By subscribing, you authorize us to charge your payment method on a recurring basis at the plan interval you selected, until you cancel.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You may cancel your subscription at any time by contacting us at{' '}
          <Link href="mailto:support@thewarriortribe.xyz" color="primary">support@thewarriortribe.xyz</Link>. Cancellation takes effect at the end of the current billing period. We do not offer refunds for partial billing periods, except where required by applicable law.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We reserve the right to change subscription pricing. We will provide at least 30 days' notice of any price increase via the email address associated with your account.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>5. Acceptable Use</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You agree to use the Service only for its intended purpose and in accordance with these Terms. You must not:
        </Typography>
        <ul>
          <li style={{ marginBottom: 8 }}>
            <Typography variant="body1">Attempt to circumvent, disable, or interfere with the extension's blocking functionality.</Typography>
          </li>
          <li style={{ marginBottom: 8 }}>
            <Typography variant="body1">Reverse engineer, decompile, or attempt to extract the source code of the Service.</Typography>
          </li>
          <li style={{ marginBottom: 8 }}>
            <Typography variant="body1">Use the Service in any way that violates applicable laws or regulations.</Typography>
          </li>
          <li style={{ marginBottom: 8 }}>
            <Typography variant="body1">Attempt to gain unauthorized access to other users' accounts or data.</Typography>
          </li>
          <li style={{ marginBottom: 8 }}>
            <Typography variant="body1">Use automated means to interact with the Service without our prior written consent.</Typography>
          </li>
        </ul>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>6. Intellectual Property</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          All content on the Service — including but not limited to text, graphics, logos, and software — is the property of The Warrior Tribe or its content licensors and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works from any content on the Service without our express written permission.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The blocklist maintained by the extension incorporates data from the StevenBlack hosts project, which is released under the MIT license.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>7. Disclaimers</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, error-free, or that the blocklist will catch every adult website. The effectiveness of blocking depends on the comprehensiveness of the blocklist, which is updated regularly but cannot guarantee complete coverage.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The educational content on this site — including blog articles about habit science and behavioral research — is provided for informational purposes only and does not constitute medical or psychological advice.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>8. Limitation of Liability</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          To the fullest extent permitted by applicable law, The Warrior Tribe and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Service. Our total liability to you for any claim arising from these Terms or your use of the Service shall not exceed the amount you paid to us in the 12 months preceding the claim.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>9. Termination</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We reserve the right to suspend or terminate your access to the Service at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, third parties, or the Service. You may stop using the Service at any time by uninstalling the extension and cancelling your subscription.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>10. Changes to These Terms</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may update these Terms from time to time. When we do, we will update the "Last updated" date above. If we make material changes, we will notify you via the email associated with your account. Your continued use of the Service after changes take effect constitutes acceptance of the updated Terms.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>11. Governing Law</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          These Terms are governed by and construed in accordance with applicable law. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts in the jurisdiction where The Warrior Tribe is operated.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h5" gutterBottom>12. Contact</Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Questions about these Terms should be directed to{' '}
          <Link href="mailto:support@thewarriortribe.xyz" color="primary">support@thewarriortribe.xyz</Link>.
          See also our <Link href="/privacy" color="primary">Privacy Policy</Link>.
        </Typography>
      </div>
    </Container>
  );
}
